import React, { useCallback, useState } from 'react';

import {
  Combobox,
  ComboboxDropdown,
  ComboboxOption,
  ComboboxSelectButton,
  ComboboxSelectCheck,
  Icon,
  useToast,
} from '@eluve/components';
import {
  type UiLanguageCode,
  useTranslation,
  useUiLanguage,
} from '@eluve/i18n';
import { useAnalytics } from '@eluve/posthog-react';
import {
  useAssignedTenantIdFromParams,
  useUserIdFromSession,
} from '@eluve/session-helpers';

import { useUpdateUiLanguage } from './useUpdateUiLanguage';

export const InterfaceLanguageSelector: React.FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const { language, languages } = useUiLanguage();
  const tenantId = useAssignedTenantIdFromParams();
  const userId = useUserIdFromSession();
  const analytics = useAnalytics('user_settings');
  const [isUpdating, setIsUpdating] = useState(false);

  const clearPending = useCallback(() => setIsUpdating(false), []);

  const onSuccess = useCallback(
    (code: UiLanguageCode) => {
      analytics.capture('interface_language_update', {
        tenantId,
        userId,
        language: code,
      });
      clearPending();
    },
    [analytics, tenantId, userId, clearPending],
  );

  const onError = useCallback(() => {
    toast.error(t('common.languageUpdateFailed'));
    clearPending();
  }, [toast, t, clearPending]);

  const setUiLanguage = useUpdateUiLanguage(tenantId, userId, {
    onSuccess,
    onError,
    onSkip: clearPending,
  });

  const handleSelect = useCallback(
    (code: UiLanguageCode) => {
      setIsUpdating(true);
      setUiLanguage(code);
    },
    [setUiLanguage],
  );

  const currentLabel =
    languages.find((l) => l.code === language)?.nativeLabel ?? language;

  return (
    <Combobox disabled={isUpdating}>
      <ComboboxSelectButton
        variant="solid"
        className="w-full justify-between"
        aria-label={`${t('settings.audio.interfaceLanguageLabel')}: ${currentLabel}`}
        dropdownIcon={
          <Icon name="ChevronDown" className="ml-2 shrink-0 opacity-50" />
        }
      >
        {currentLabel}
      </ComboboxSelectButton>
      <ComboboxDropdown
        searchPlaceholder={t(
          'settings.audio.interfaceLanguageSearchPlaceholder',
        )}
        className="min-w-52"
      >
        {languages.map((option) => (
          <ComboboxOption
            key={option.code}
            searchValue={`${option.nativeLabel} ${option.label}`}
            onSelect={() => handleSelect(option.code)}
          >
            <ComboboxSelectCheck
              className="mr-3"
              selected={option.code === language}
            />
            {option.nativeLabel}
          </ComboboxOption>
        ))}
      </ComboboxDropdown>
    </Combobox>
  );
};
